import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import api from '@api/api';
import { ReactComponent as ChargeAmountIcon } from '@assets/svg/station-detail/charge-amount.svg';
import { ReactComponent as GunIcon } from '@assets/svg/station-detail/gun.svg';
import { ReactComponent as IncomeIcon } from '@assets/svg/station-detail/income.svg';
import { ReactComponent as OrderIcon } from '@assets/svg/station-detail/order.svg';
import { ReactComponent as PileIcon } from '@assets/svg/station-detail/pile.svg';
import { ReactComponent as RealtimePowerIcon } from '@assets/svg/station-detail/realtime-power.svg';
import { ReactComponent as TotalPowerIcon } from '@assets/svg/station-detail/total-power.svg';
import ActionButton from '@components/ActionButton/ActionButton';
import CustomTable from '@components/CustomTable/CustomTable';
import TextEllipsis from '@components/TextEllipsis/TextEllipsis';
import { CommonResponseObj } from '@models/common';
import ExtendedGlobalToken from '@models/extendedThemeToken';
import { GunInfo } from '@models/gun';
import { StationData } from '@models/station';
import { formatDuration, formatNumberWithDigits } from '@utils/common';
import { AUTO_UPDATE_INTERVAL } from '@utils/const';
import { App, Card, Col, Row, Tag, Typography, theme } from 'antd';
import ReactECharts from 'echarts-for-react';

import type { ColumnsType } from 'antd/lib/table';

import './RealTimeStationDetail.scss';

const { useToken } = theme;

interface OperationalTrendData {
  balanceList: string[];
  billList: string[];
  powerList: string[];
  timeList: string[];
}
interface IconCardProps {
  label: string;
  value: string;
  icon: JSX.Element;
  textColor?: string;
}
interface ColorCardProps {
  value: string;
  color: string;
  label: string;
}

interface OrderTrendChartProps {
  data: { billList: string[]; timeList: string[] };
}
interface ChargeAmountAndIncomeTrendChartProps {
  data: { balanceList: string[]; timeList: string[]; powerList: string[] };
}

const API = {
  GET_STATION_DETAIL: '/api/operation-service/monitor/station',
  GET_STATION_OPERATIONAL_TREND:
    '/api/operation-service/monitor/getStationReport',
};

const initialOperationalTrendData: OperationalTrendData = {
  balanceList: [],
  billList: [],
  powerList: [],
  timeList: [],
};

function getNextDay(dateString: string) {
  // Create a date object from the input string
  const date = new Date(dateString);

  // Add one day to the date
  date.setDate(date.getDate() + 1);

  // Format the year, month, and day as needed, adding leading zeros if necessary
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');

  // Return the new date string in the specified format
  return `${year}/${month}/${day}`;
}

function getCommonChartOptions(timeList: string[]) {
  return {
    grid: {
      left: '1%',
      right: '1%',
      top: '15%',
      bottom: '1%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: timeList || [],
      axisLabel: {
        interval: 5,
        formatter(value: string) {
          return value.substring(5).replace(/\//g, '-');
        },
        color: '#98A2B3',
        fontSize: 8,
      },
      axisLine: {
        show: false,
        lineStyle: {
          color: '#E4E7EC',
        },
      },
      axisTick: {
        interval: 0,
      },
    },
  };
}

function OrderTrendChart(props: OrderTrendChartProps) {
  const { t } = useTranslation();
  const {
    data: { timeList, billList },
  } = props;

  const option = {
    ...getCommonChartOptions(timeList),
    tooltip: {
      className: 'StationDetail__customTooltip',
      trigger: 'axis',
      formatter(...params: any[]) {
        // console.log('params[0]', params[0]);
        const date = params[0][0].axisValue;
        const bill = formatNumberWithDigits(Number(params[0][0].value) || 0, 0);

        return `
        <div class="content">
          <div class="date">${date}</div>
          <div class="item">
            <div class="point" style="background-color: #7B61FF;"></div>
            <div class="label">${t('RealTimeStationDetail.OrdersUnits')}</div>
            <div class="value">${bill}</div>
          </div>
        </div>
        `;
      },
    },
    legend: {
      data: [t('RealTimeStationDetail.Orders')],
      padding: 10,
    },

    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: `{value} ${t('RealTimeStationDetail.Units')}`,
        color: '#98A2B3',
        fontSize: 10,
      },
    },
    series: [
      {
        name: t('RealTimeStationDetail.Orders'),
        type: 'line',
        data: billList || [],
        showSymbol: false,
        color: '#7B61FF',
      },
    ],
  };

  return (
    <ReactECharts
      option={option}
      style={{ height: '15.6309375rem' }}
      opts={{ renderer: 'svg' }}
    />
  );
}

function ChargeAmountAndIncomeTrendChart(
  props: ChargeAmountAndIncomeTrendChartProps,
) {
  const { t } = useTranslation();
  const {
    data: { timeList, powerList, balanceList },
  } = props;

  const option = {
    ...getCommonChartOptions(timeList),
    tooltip: {
      className: 'StationDetail__customTooltip',
      trigger: 'axis',
      formatter(...params: any[]) {
        // console.log('params[0]', params[0]);
        const date = params[0][0].axisValue;
        const chargeAmount = formatNumberWithDigits(
          Number(params[0][0].value) || 0,
          0,
        );
        const income = formatNumberWithDigits(
          Number(params[0][1].value) || 0,
          0,
        );
        return `
        <div class="content">
          <div class="date">${date}</div>
          <div class="item">
            <div class="point" style="background-color: #5F90EF;"></div>
            <div class="label">${t(
              'RealTimeStationDetail.ChargingEnergyKWh',
            )}</div>
            <div class="value">${chargeAmount}</div>
          </div>
          <div class="item">
            <div class="point" style="background-color: #F29500;"></div>
            <div class="label">${t('RealTimeStationDetail.RevenueDollar')}</div>
            <div class="value">${income}</div>
          </div>
        </div>
        `;
      },
    },

    legend: {
      data: [
        t('RealTimeStationDetail.ChargingEnergy'),
        t('RealTimeStationDetail.Revenue'),
      ],
      padding: 10,
    },

    grid: {
      left: '1%',
      right: '1%',
      top: '15%',
      bottom: '1%',
      containLabel: true,
    },
    yAxis: [
      {
        alignTicks: true,
        type: 'value',
        name: null,
        min: 0,
        position: 'left',
        axisLabel: {
          formatter: (value: number) =>
            value >= 1000
              ? `${value / 1000} ${t('RealTimeStationDetail.ThousandsDollar')}`
              : `${value} ${t('Common.CurrencyUnit')}`,
          color: '#98A2B3',
          fontSize: 10,
        },
      },
      {
        alignTicks: true,
        type: 'value',
        name: null,
        min: 0,
        position: 'right',
        axisLabel: {
          formatter: '{value} kWh',
          color: '#98A2B3',
          fontSize: 10,
        },
      },
    ],
    series: [
      {
        name: t('RealTimeStationDetail.ChargingEnergy'),
        type: 'bar',
        data: powerList || [],
        color: '#CAE6FF',
        yAxisIndex: 1,
      },
      {
        name: t('RealTimeStationDetail.Revenue'),
        type: 'line',
        yAxisIndex: 0,
        data: balanceList || [],
        showSymbol: false,
        color: '#F29500',
      },
    ],
  };

  return (
    <ReactECharts
      option={option}
      style={{ height: '15.6309375rem' }}
      opts={{ renderer: 'svg' }}
    />
  );
}

function IconCard({
  label,
  value,
  icon,
  textColor = undefined,
}: IconCardProps) {
  return (
    <div className="IconCard">
      <div className="IconCard__icon">{icon ?? ''}</div>
      <div>
        <div className="IconCard__label">{label ?? '-'}</div>
        <div
          className="IconCard__value"
          style={textColor ? { color: textColor } : {}}
        >
          {value ?? '0'}
        </div>
      </div>
    </div>
  );
}

function ColorCard(props: ColorCardProps) {
  const { value, color, label } = props;
  return (
    <div className="ColorCard">
      <div className="ColorCard__value">{value ?? '0'}</div>
      <div className="ColorCard__info">
        <div className={`ColorCard__color ${color ?? ''}`} />
        <div className="ColorCard__label">{label ?? '-'}</div>
      </div>
    </div>
  );
}

function RealTimeStationDetail() {
  const navigate = useNavigate();
  const { token } = useToken() as unknown as ExtendedGlobalToken;
  const { message } = App.useApp();
  const [stationData, setStationData] = useState<StationData | null>(null);
  const [operationalTrendData, setOperationalTrendData] =
    useState<OperationalTrendData>(initialOperationalTrendData);

  // useRef to store the timeout ID for auto-updates so it can be cleared on unmount
  const stationDetailTimeoutRef = useRef(undefined as any);
  const operationalTrendTimeoutRef = useRef(undefined as any);
  const stationDetailAbortRef = useRef<AbortController | null>(null);
  const operationalTrendAbortRef = useRef<AbortController | null>(null);

  const { t } = useTranslation();

  const { id } = useParams(); // `id` is the name of the parameter in the route path

  const maintenanceViewList = [
    { key: 'availableGunCount', label: t('RealTimeStationDetail.Idle') },
    { key: 'reservedGunCount', label: t('RealTimeStationDetail.Reserved') },
    {
      key: 'preparingGunCount',
      label: t('RealTimeStationDetail.PreparingCharge'),
    },
    { key: 'chargingGunCount', label: t('Model.Charging') },
    {
      key: 'suspendedEVSEGunCount',
      label: t('RealTimeStationDetail.PausedByPile'),
    },
    {
      key: 'suspendedEVGunCount',
      label: t('RealTimeStationDetail.PausedByVehicle'),
    },
    {
      key: 'finishingGunCount',
      label: t('RealTimeStationDetail.ChargingStop'),
    },
    { key: 'faultedGunCount', label: t('RealTimeStationDetail.Faulty') },
    { key: 'unavailableGunCount', label: t('RealTimeStationDetail.Disabled') },
    { key: 'offlineGunCount', label: t('RealTimeStationDetail.Offline') },
  ];

  useEffect(() => {
    if (!id) {
      navigate('/');
    }
  }, [id]);

  const { Title } = Typography;

  const realtimePileGunColumn: ColumnsType<GunInfo> = [
    {
      key: 'parkingLotName',
      title: t('Common.ParkingSpace'),
      width: '7.5rem',
      dataIndex: 'parkingLotName',
      render: (parkingLotName) => <TextEllipsis text={parkingLotName || '-'} />,
    },
    {
      key: 'gunName',
      title: t('Common.GunName'),
      width: '10.75rem',
      dataIndex: 'gunName',
      render: (gunName) => <TextEllipsis text={gunName || '-'} />,
    },
    {
      key: 'socketType',
      title: t('Common.GunSocketSpec'),
      width: '7.5rem',
      dataIndex: 'socketType',
      render: (socketType) => <Tag>{socketType || '-'}</Tag>,
    },
    {
      key: 'pileName',
      title: t('Common.PileName'),
      width: '10.75rem',
      dataIndex: 'pileName',
      render: (pileName) => <TextEllipsis text={pileName || '-'} />,
    },
    {
      key: 'pileName',
      title: t('Common.PileRatedPowerType'),
      width: '10.75rem',
      render: (record) => (
        <>
          {record.currentMode} -{' '}
          {formatNumberWithDigits(record.ratingPower || 0, 0)} kW
        </>
      ),
    },
    {
      key: 'gunName',
      title: t('Common.MemberName'),
      width: '6.25rem',
      render: (record) => (
        <TextEllipsis text={record.transactionalData?.memberName || '-'} />
      ),
    },
    {
      key: 'energyOffered',
      title: t('RealTimeStationDetail.ChargingEnergyKWh'),
      width: '9rem',
      render: (record) =>
        record.transactionalData?.energyOffered === undefined
          ? '-'
          : formatNumberWithDigits(record.transactionalData.energyOffered, 3),
    },
    {
      key: 'duration',
      title: t('RealTimeStationDetail.ChargingTime'),
      width: '7.5rem',
      render: (record) =>
        formatDuration(record.transactionalData?.duration || 0),
    },
    {
      key: 'operationViewStatus',
      title: t('RealTimeStationDetail.GunOperatingStatus'),
      fixed: 'right',
      width: '8.875rem',
      dataIndex: 'operationViewStatus',
      align: 'center',
      render: (operationViewStatus: string) => (
        <div
          className="StationDetail__realtimePileGunStatus__gunStatus"
          style={{
            backgroundColor:
              token.GunOperationalStatusColors[
                operationViewStatus.toUpperCase()
              ],
          }}
        />
      ),
    },
    {
      key: 'anyAlarm',
      title: t('RealTimeStationDetail.GunAlertStatus'),
      fixed: 'right',
      width: '8.875rem',
      dataIndex: 'anyAlarm',
      align: 'center',
      render: (anyAlarm) => (
        <div
          className="StationDetail__realtimePileGunStatus__gunStatus"
          style={{
            backgroundColor: anyAlarm ? token.colorError : token.colorSuccess,
          }}
        />
      ),
    },
    {
      key: 'action',
      title: t('Common.Action'),
      fixed: 'right',
      width: '5rem',
      align: 'center',
      render: (record) => (
        <Link
          to={`/Equipment/RealTimeStationGun/RealTimeChargingStationMonitoring/${id}/${record.pileId}`}
          state={{ gunId: record.gunId }}
        >
          <ActionButton preset="view" onClick={() => {}} />
        </Link>
      ),
    },
  ];

  async function fetchStationDetail(initialDelay = AUTO_UPDATE_INTERVAL) {
    if (!id) return;

    // 取消上一個請求
    stationDetailAbortRef.current?.abort();

    const controller = new AbortController();
    stationDetailAbortRef.current = controller;

    let delay = initialDelay;

    try {
      const res = await api.get(`${API.GET_STATION_DETAIL}?stationId=${id}`, {
        signal: controller.signal,
      });

      const { code, data, msg }: CommonResponseObj = res.data;
      if (code !== 0) {
        message.error(msg);
        throw new Error(msg);
      }

      setStationData(data);
      delay = AUTO_UPDATE_INTERVAL;
    } catch (err) {
      console.warn(err, new Date());
      delay = Math.min(delay * 2, 60000); // 指數退避，最大 60 秒
    }

    // 自動更新
    stationDetailTimeoutRef.current = setTimeout(
      () => fetchStationDetail(delay),
      delay,
    );
  }

  async function fetchStationOperationalTrend(
    initialDelay = AUTO_UPDATE_INTERVAL,
  ) {
    if (!id) return;

    // 取消前一個請求
    operationalTrendAbortRef.current?.abort();

    const controller = new AbortController();
    operationalTrendAbortRef.current = controller;

    let delay = initialDelay;

    try {
      const formData = new FormData();
      formData.append('stationId', id);

      const res = await api.post(API.GET_STATION_OPERATIONAL_TREND, formData, {
        signal: controller.signal,
      });

      const { data } = res;
      if (!data) {
        message.error(t('Common.OperationFailed'));
        throw new Error('操作失敗');
      }

      const { balanceList, billList, powerList, timeList } = data;
      const extraDay = getNextDay(timeList[timeList.length - 1]);
      setOperationalTrendData({
        balanceList,
        billList,
        powerList,
        timeList: [...timeList, extraDay],
      });
      delay = AUTO_UPDATE_INTERVAL;
    } catch (err) {
      console.warn(err, new Date());
      delay = Math.min(delay * 2, 60000);
    }

    // 自動更新
    operationalTrendTimeoutRef.current = setTimeout(
      () => fetchStationOperationalTrend(delay),
      delay,
    );
  }

  useEffect(() => {
    fetchStationDetail();
    fetchStationOperationalTrend();

    return () => {
      clearTimeout(stationDetailTimeoutRef.current);
      clearTimeout(operationalTrendTimeoutRef.current);

      stationDetailAbortRef.current?.abort();
      operationalTrendAbortRef.current?.abort();
    };
  }, []);

  return (
    <div className="StationDetail">
      <Title className="StationDetail__title">
        {stationData?.stationSummary?.stationMetadata?.name ||
          t('Common.Loading')}
      </Title>
      <Row gutter={[24, 16]} className="StationDetail__cards">
        <Col span={10}>
          <Card>
            <div className="StationDetail__cardTitle">
              {t('RealTimeStationDetail.EnergySummary')}
            </div>
            <Row gutter={16}>
              <Col span={12}>
                <IconCard
                  label={t('RealTimeStationDetail.RatedPowerKW')}
                  value={formatNumberWithDigits(
                    stationData?.stationDetail?.totalRatingPower || 0,
                    0,
                  )}
                  icon={<TotalPowerIcon />}
                />
              </Col>
              <Col span={12}>
                <IconCard
                  label={t('RealTimeStationDetail.PowerKW')}
                  value={formatNumberWithDigits(
                    stationData?.stationDetail?.totalRealtimeOutputPower || 0,
                    3,
                  )}
                  icon={<RealtimePowerIcon />}
                />
              </Col>
            </Row>
          </Card>
        </Col>
        <Col span={14}>
          <Card>
            <div className="StationDetail__cardTitle">
              {t('RealTimeStationDetail.TodaySummary')}
            </div>
            <Row gutter={16}>
              <Col span={7}>
                <IconCard
                  label={t('RealTimeStationDetail.OrdersUnits')}
                  value={formatNumberWithDigits(
                    stationData?.stationSummary?.dailySummary?.billCompleted ||
                      0,
                    0,
                  )}
                  icon={<OrderIcon />}
                  textColor="#7B61FF"
                />
              </Col>
              <Col span={10}>
                <IconCard
                  label={t('RealTimeStationDetail.ChargingEnergyKWh')}
                  value={formatNumberWithDigits(
                    stationData?.stationSummary?.dailySummary?.energyOffered ||
                      0,
                    3,
                  )}
                  icon={<ChargeAmountIcon />}
                  textColor={token.colorInfo}
                />
              </Col>
              <Col span={7}>
                <IconCard
                  label={t('RealTimeStationDetail.RevenueDollar')}
                  value={formatNumberWithDigits(
                    stationData?.stationSummary?.dailySummary?.income || 0,
                    0,
                  )}
                  icon={<IncomeIcon />}
                  textColor={token.colorWarning}
                />
              </Col>
            </Row>
          </Card>
        </Col>
        <Col span={10}>
          <Card>
            <div className="StationDetail__cardTitle">
              {t('RealTimeStationDetail.EquipmentSummary')}
            </div>
            <Row gutter={16} className="StationDetail__chargingEquipment">
              <Col span={12}>
                <IconCard
                  label={t('RealTimeStationDetail.PilesUnits')}
                  value={formatNumberWithDigits(
                    stationData?.stationSummary?.maintenanceView
                      ?.totalPileCount || 0,
                    0,
                  )}
                  icon={<PileIcon />}
                />
              </Col>
              <Col span={12}>
                <IconCard
                  label={t('RealTimeStationDetail.GunsUnits')}
                  value={formatNumberWithDigits(
                    stationData?.stationSummary?.maintenanceView
                      ?.totalGunCount || 0,
                    0,
                  )}
                  icon={<GunIcon />}
                />
              </Col>
            </Row>
            <div className="StationDetail__iconCardGroup">
              <Row gutter={[20, 16]}>
                {maintenanceViewList.map((item) => {
                  let value = 0;
                  if (stationData) {
                    const { maintenanceView } = stationData.stationSummary;
                    value =
                      maintenanceView[
                        item.key as keyof typeof maintenanceView
                      ] || 0;
                  }
                  return (
                    <Col span={12} key={item.key}>
                      <ColorCard
                        value={formatNumberWithDigits(value, 0)}
                        label={item.label}
                        color={item.key}
                      />
                    </Col>
                  );
                })}
              </Row>
            </div>
          </Card>
        </Col>
        <Col span={14}>
          <Card>
            <div className="StationDetail__cardTitle">
              {t('RealTimeStationDetail.MonthlyTrend')}
            </div>
            <div className="StationDetail__chartWrapper">
              <OrderTrendChart data={operationalTrendData} />
            </div>
            <div className="StationDetail__chartWrapper">
              <ChargeAmountAndIncomeTrendChart data={operationalTrendData} />
            </div>
          </Card>
        </Col>
        <Col span={24}>
          <Card>
            <div className="StationDetail__cardTitle">
              {t('RealTimeStationDetail.PileGunStatus')}
            </div>
            <CustomTable<GunInfo>
              columns={realtimePileGunColumn}
              dataSource={stationData?.stationDetail.gunInfos || []}
              rowKey="gunId"
              paginationConfig={false}
              scroll={{
                x: '72.75rem',
              }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default RealTimeStationDetail;
